import {
  Apps,
  BarChartSharp,
  Group,
  HelpOutline,
  ShieldOutlined,
  WorkOutline,
} from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useEffect } from 'react';
import { RiFileSettingsLine } from 'react-icons/ri';
import { Item, theme, WsColors } from 'wisestep-base';

import SettingsIndex, { IOption } from '../../../components/SettingsIndex';
import { useWorkspaceContext } from '../../../context';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { SettingsMenuItems } from '../types';
const SettingsMenuOptions = [
  {
    icon: <Group />,
    label: 'Teams and Users',
    url: '/app-settings/' + SettingsMenuItems.TEAMS_AND_USERS,
  },
  {
    icon: <WorkOutline />,
    label: 'Jobs',
    url: '/app-settings/' + SettingsMenuItems.JOBS,
  },
  {
    icon: <BarChartSharp />,
    label: 'Analytics',
    url: '/app-settings/' + SettingsMenuItems.ANALYTICS,
  },
  {
    icon: <WorkOutline />,
    label: 'CRM',
    url: '/app-settings/' + SettingsMenuItems.CRM,
  },
  {
    icon: <Group />,
    label: 'Account',
    url: '/app-settings/' + SettingsMenuItems.ACCOUNT,
  },
  {
    icon: <ShieldOutlined />,
    label: 'Data Privacy',
    url: '/app-settings/' + SettingsMenuItems.DATA_PRIVACY,
  },
  {
    icon: <Apps />,
    label: 'Apps and Integrations',
    url: '/app-settings/' + SettingsMenuItems.APPS_AND_INTEGRATIONS,
  },
  {
    label: 'Career Pages',
    icon: <RiFileSettingsLine size={23} />,
    url: '/app-settings/' + SettingsMenuItems.CAREER_PAGES,
  },
  {
    icon: <Group />,
    label: 'Clients',
    url: '/app-settings/' + SettingsMenuItems.CLIENTS,
  },
  {
    icon: <HelpOutline />,
    label: 'Automation',
    url: '/app-settings/' + SettingsMenuItems.AUTOMATION,
  },
  {
    icon: <HelpOutline />,
    label: 'Help and Knowledge',
    url: '/app-settings/' + SettingsMenuItems.HELP_AND_KNOWLEDGE,
  },
];
const SettingsMenuOptionsUser = [
  {
    icon: <Group />,
    label: 'Teams and Users',
    url: '/app-settings/' + SettingsMenuItems.TEAMS_AND_USERS,
  },
  {
    icon: <Group />,
    label: 'Account',
    url: '/app-settings/' + SettingsMenuItems.ACCOUNT,
  },
  {
    icon: <Apps />,
    label: 'Apps and Integrations',
    url: '/app-settings/' + SettingsMenuItems.APPS_AND_INTEGRATIONS,
  },
];

export interface ISettingsMenuProps {
  options?: IOption[];
  getSelectedOption?: (option: number) => void;
  getSelectedSubOption?: (subOption: number) => void;
}

const SettingsMenu = ({ options, getSelectedOption, getSelectedSubOption }: ISettingsMenuProps) => {
  const workspaceContext = useWorkspaceContext();

  const { access: jobsAtsControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.JOB_FIELDS_CUSTOMISATION,
    AtsCustomAPIAccess.REASON_FOR_CLOSING_JOB_SETTING,
    AtsCustomAPIAccess.JOB_ADMIN_TEAM_AS_MANDATORY,
    AtsCustomAPIAccess.DATA_DOWNLOAD_SETTINGS_IN_JOBS,
    AtsCustomAPIAccess.JOB_ATTRIBUTE_VISIBILITY_SETTINGS,
  ]);

  const { access: crmAtsControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MAPPING_FIELDS_CUSTOMISATION,
    AtsCustomAPIAccess.SUBMISSION_FIELDS_CUSTOMISATION,
    AtsCustomAPIAccess.RECRUITER_ACTIONS,
    AtsCustomAPIAccess.CANDIDATE_RESPONSES,
    AtsCustomAPIAccess.STAGE_STATUS_CHANGES,
    AtsCustomAPIAccess.DATA_DOWNLOAD_SETTINGS_IN_CANDIDATES,
    AtsCustomAPIAccess.PAGINATION_SETTINGS,
  ]);

  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.CAREER_PAGE_SETTINGS,
    AtsCustomAPIAccess.CLIENT_SETTINGS,
    AtsCustomAPIAccess.INVOICE_SETTINGS,
    AtsCustomAPIAccess.PRISM_SETTINGS,
    AtsCustomAPIAccess.DATA_SHARING_SETTINGS,
  ]);

  const jobAcccess =
    jobsAtsControl[0] ||
    jobsAtsControl[1] ||
    jobsAtsControl[2] ||
    jobsAtsControl[3] ||
    jobsAtsControl[4];

  const crmAcccess =
    crmAtsControl[0] ||
    crmAtsControl[1] ||
    crmAtsControl[2] ||
    crmAtsControl[3] ||
    crmAtsControl[4] ||
    crmAtsControl[5] ||
    crmAtsControl[6];

  useEffect(() => {
    if (jobAcccess && !SettingsMenuOptionsUser.some((option) => option.label === 'Jobs')) {
      SettingsMenuOptionsUser.push({
        icon: <WorkOutline />,
        label: 'Jobs',
        url: '/app-settings/' + SettingsMenuItems.JOBS,
      });
    }
    if (crmAcccess && !SettingsMenuOptionsUser.some((option) => option.label === 'CRM')) {
      SettingsMenuOptionsUser.push({
        icon: <WorkOutline />,
        label: 'CRM',
        url: '/app-settings/' + SettingsMenuItems.CRM,
      });
    }
    if (
      atsCustomAccessControl[0] &&
      !SettingsMenuOptionsUser.some((option) => option.label === 'Career Pages')
    ) {
      SettingsMenuOptionsUser.push({
        label: 'Career Pages',
        icon: <RiFileSettingsLine size={23} />,
        url: '/app-settings/' + SettingsMenuItems.CAREER_PAGES,
      });
    }
    if (
      atsCustomAccessControl[1] &&
      !SettingsMenuOptionsUser.some((option) => option.label === 'Clients')
    ) {
      SettingsMenuOptionsUser.push({
        icon: <Group />,
        label: 'Clients',
        url: '/app-settings/' + SettingsMenuItems.CLIENTS,
      });
    }
    if (
      atsCustomAccessControl[2] &&
      !SettingsMenuOptionsUser.some((option) => option.label === 'Invoice')
    ) {
      SettingsMenuOptionsUser.push({
        icon: <DescriptionOutlinedIcon />,
        label: 'Invoice',
        url: '/app-settings/' + SettingsMenuItems.INVOICE_MODULE,
      });
    }
    if (!SettingsMenuOptionsUser.some((option) => option.label === 'FAQs')) {
      SettingsMenuOptionsUser.push({
        icon: <Apps />,
        label: 'FAQs',
        url: '/app-settings/' + SettingsMenuItems.FAQS,
      });
    }
  }, [atsCustomAccessControl, crmAcccess, jobAcccess]);

  const variableOptions = workspaceContext.owns ? SettingsMenuOptions : SettingsMenuOptionsUser;
  return (
    <Item
      sx={{
        p: 0,
        pb: 12,
        backgroundColor: WsColors.White,
        boxShadow: `${theme.spacing(0)} ${theme.spacing(0.125)} ${theme.spacing(
          0.375
        )} rgba(0, 0, 0, 0.12)`,
      }}
    >
      <SettingsIndex
        options={options || variableOptions}
        getSelectedOption={getSelectedOption}
        getSelectedSubOption={getSelectedSubOption}
      />
    </Item>
  );
};

export default SettingsMenu;
